<template>
  <div class="settings">
    <div v-if="!loadingCurrentUser">
      <settings-user :user="currentUser || ''" :loading="loadingUsers" @save="updateCurrentUser"></settings-user>
      <settings-alarms :loading="loadingUsers" :alarms="currentUser || ''" @save="updateAlarams"></settings-alarms>
    </div>
    <loader v-else></loader>
  </div>
</template>

<script>
import SettingsUser from './blocks/SettingsUser'
import SettingsAlarms from './blocks/SettingsAlarms'
import Loader from '@/components/Loader'

import { mapActions, createNamespacedHelpers } from 'vuex'

const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'UserSettings',
  components: {
    SettingsUser,
    SettingsAlarms,
    Loader
  },
  data() {
    return {
      currentUser: {},
      loadingCurrentUser: false,
      users: [],
      loadingUsers: false,
      customModules: [],
      loadingCustomModules: false,
      misc: {},
      miscParameters: [],
      isMiscLoading: false
    }
  },
  computed: {
    ...mapUserState({
      stateUser: state => {
        return state.user
      }
    }),
    isAdmin() {
      if (this.currentUser) {
        return this.currentUser.role === 'ADMIN'
      }
      return false
    }
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    ...mapActions('general', ['setUser', 'setUserAlarms']),
    saveMisc() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/misc`
      this.isMiscLoading = true
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          misc_settings: this.misc
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: `${this.$t('notification.updated', {
              msg: this.$t(`settings.user.misc`)
            })}.`,
            type: 'success'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.isMiscLoading = false
        })
    },
    fetchMisc() {
      if (!this.isMiscLoading) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/misc`
        this.isMiscLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.misc = data.config
            this.miscParameters = data.properties.parameters
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.isMiscLoading = false
          })
      }
    },
    fetchCustomModules() {
      if (!this.loadingCustomModules) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/modules`
        this.loadingCustomModules = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.customModules = data
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingCustomModules = false
          })
      }
    },
    updateCurrentUser(item) {
      this.setUser(item)
      this.updateUser(item)
    },
    updateAlarams(item) {
      this.setUserAlarms(item)
      this.updateUser(this.stateUser)
    },
    updateUser(item) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/user`
      this.isUpdateUserLoading = true
      const name = item.name
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          user_config: item
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$emit('refetch-users')
          this.notifyUser({
            name: name,
            status: 'updated'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.isUpdateUserLoading = false
          this.isUpdateUser = false
        })
    },
    fetchCurrentUser() {
      if (!this.loadingCurrentUser) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/user`
        this.loadingCurrentUser = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.currentUser = data
            this.setUser(this.currentUser)
            this.setUserAlarms(this.currentUser.alarms)
            if (this.isAdmin) {
              this.fetchUsers()
              this.fetchCustomModules()
              this.fetchMisc()
            }
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingCurrentUser = false
          })
      }
    },
    fetchUsers() {
      if (!this.loadingUsers) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/users`
        this.loadingUsers = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.users = data
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingUsers = false
          })
      }
    },
    notifyUser(variables) {
      this.$notify({
        message: `${this.$t('notification.userWas', {
          user: variables.name,
          status: this.$t(`notification.user.${variables.status}`)
        })}.`,
        type: 'success'
      })
    }
  }
}
</script>
